<script lang="ts">
  import "../../app.css";
  import type { Snippet } from "svelte";
  import { onMount, onDestroy } from "svelte";
  import { ModeWatcher, setMode } from "mode-watcher";
  import { theme } from "$lib/state/user-preferences.svelte";

  import { authStore } from "$lib/stores";
  import { writable } from "svelte/store";
  import { page } from "$app/state";

  import { MetaTags } from "$lib/components";
  import Header from "$lib/components/layout/header/headerLegacy.svelte";
  import { AppHeader as NewAppHeader } from "$lib/components/landing/redesigned/components/header";
  import NewFooterBig from "$lib/components/layout/footer/footer-big-new.svelte";
  import * as Sidebar from "$lib/components/ui/sidebar-custom";
  import SidebarMenu from "$lib/components/layout/sidebar";
  import LandingSidebar from "$lib/components/landing/sidebar/landingSidebar.svelte";
  import FontLoader from "$lib/components/FontLoader.svelte";

  import CommandMenu from "$lib/components/dialogs/commandMenu.svelte";
  import GlobalHotkeys from "$lib/utility/globalHotkeys";
  import { cn } from "$lib/utility";

  import { initEnvironment } from "$lib/utility/initEnvironment";
  import ShortcutGuide from "$lib/components/basic/shortcutGuide/shortcutGuide.svelte";
  import { homepageStore, setShortcutMode } from "$lib/stores/homepageStore";
  import { tableStore } from "$lib/stores/table-store";
  import { routes } from "$lib/routes";

  let { data, children }: { data: any; children: Snippet } = $props();
  let isAboutPage = $derived(page.url.pathname.startsWith("/about"));
  let isCompaniesPage = $derived(page.url.pathname.startsWith("/companies"));
  let isPeoplePage = $derived(page.url.pathname.startsWith("/people"));
  let forceBlackHeader = $derived(isCompaniesPage || isPeoplePage);
  let isTablesPage = $derived(page.url.pathname.startsWith(routes.tables));
  let isLandingPage = $derived(
    page.url.pathname == "/" ||
      page.url.pathname.startsWith("/features/") ||
      page.url.pathname.startsWith("/use-cases/") ||
      page.url.pathname.startsWith("/templates"),
  );
  let isHomePage = $derived(page.url.pathname === "/");

  let loadingLayout = writable(!data.isBot);

  let isBlog = $derived(page.url.pathname.startsWith("/blog"));

  initEnvironment(data);

  const darkModeEnforcer = (() => {
    const MAX_ENFORCEMENT_ATTEMPTS = 10;
    const MUTATION_DECAY_DELAY = 1000;

    let isActive = $state(true);
    let mutationCount = $state(0);
    let enforcementTimer = $state<number | null>(null);
    let observer = $state<MutationObserver | null>(null);

    const isDarkModeActive = (): boolean => {
      if (typeof document === "undefined") return true;

      return (
        document.body.classList.contains("dark") &&
        document.documentElement.classList.contains("dark") &&
        theme.current === "dark" &&
        theme.preference === "dark"
      );
    };

    const enforce = (): void => {
      if (!isActive || typeof document === "undefined") return;

      if (isDarkModeActive()) return;

      const elements = [document.body, document.documentElement];
      elements.forEach((el) => {
        el.classList.remove("light");
        el.classList.add("dark");
      });

      theme.current = "dark";
      theme.preference = "dark";
      setMode("dark");
    };

    const handleMutations = (mutations: MutationRecord[]): void => {
      if (!isActive) return;

      const hasClassMutation = mutations.some((m) => m.type === "attributes" && m.attributeName === "class");

      if (!hasClassMutation) return;

      if (enforcementTimer) {
        cancelAnimationFrame(enforcementTimer);
      }

      enforcementTimer = requestAnimationFrame(() => {
        if (!isActive || isDarkModeActive()) {
          enforcementTimer = null;
          return;
        }

        if (++mutationCount > MAX_ENFORCEMENT_ATTEMPTS) {
          console.warn("[Marketing Layout] Dark mode enforcement limit reached, disabling observer");
          cleanup();
          return;
        }

        enforce();

        setTimeout(() => {
          mutationCount = Math.max(0, mutationCount - 1);
        }, MUTATION_DECAY_DELAY);

        enforcementTimer = null;
      });
    };

    const init = (): void => {
      enforce();

      if (typeof document === "undefined") return;

      observer = new MutationObserver(handleMutations);

      const observeTarget = (target: Element) => {
        observer?.observe(target, {
          attributes: true,
          attributeFilter: ["class"],
        });
      };

      observeTarget(document.body);
      observeTarget(document.documentElement);
    };

    const cleanup = (): void => {
      isActive = false;

      if (enforcementTimer) {
        cancelAnimationFrame(enforcementTimer);
        enforcementTimer = null;
      }

      observer?.disconnect();
      observer = null;
    };

    return { init, cleanup, enforce };
  })();

  if (typeof document !== "undefined") {
    darkModeEnforcer.enforce();
  }

  onMount(() => {
    darkModeEnforcer.init();

    (async () => {
      if (data.isBot) {
        loadingLayout.set(false);
        return;
      }

      if (isHomePage) {
        loadingLayout.set(false);
        return;
      }

      try {
        await authStore.initUser();
      } catch (error) {
        console.error("Error during auth initialization:", error);
      } finally {
        loadingLayout.set(false);
      }
    })();

    return () => {
      darkModeEnforcer.cleanup();
    };
  });

  onDestroy(() => {
    darkModeEnforcer.cleanup();
  });
</script>

<MetaTags
  metaTitle={page.data.metaTitle || "Chatsheet | The AI Spreadsheet"}
  metaDescription={page.data.metaDescription ||
    "Chatsheet | The AI Spreadsheet. Extract PDFs, analyze files, and build custom AI's. Automate your workflow with an army of AI employees."}
  ogImage={page.data.feature_image || "/og.jpg"}
  ogType={page.data.feature_image ? "article" : "website"}
  author={page.data.author}
  canonicalUrl={"https://chatsheet.com" + page.url.pathname}
/>

<Sidebar.Provider open={false} enableKeyboardShortcut={false}>
  {#if $loadingLayout === false || data.isBot}
    <LandingSidebar collapsible={"offcanvas"} theme="dark" />
    <div class={cn("custom-scrollbar flex w-full flex-col min-h-screen font-jakarta bg-black")}>
      <NewAppHeader
        className={cn("flex flex-shrink-0", !isBlog ? "sticky-header" : "")}
        variant={isBlog ? "dark" : "default"}
        forceBlackBackground={forceBlackHeader}
      />
      <main class={cn("w-full flex-grow", isAboutPage ? "p-5 md:p-6" : "")}>
        {@render children()}
      </main>
    </div>
  {/if}
</Sidebar.Provider>

{#if $loadingLayout === false || data.isBot}
  <NewFooterBig />
{/if}

<FontLoader />
<GlobalHotkeys />
<CommandMenu />
<ModeWatcher />
<!-- <ShortcutGuide isShortcutMode={$homepageStore.isInShortcutMode} hideOverlay="{setShortcutMode(false)}}" /> -->
